import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';

import RolesService from '../../../../data/roles-service';
import Auth from '../../../../data/auth-service';
import DataContext from '../../../../data/data-context';

const useStyles = makeStyles(theme => ({
    input: {
        width: '120px',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    select: {
        marginTop: -(theme.spacing(2)),
        marginBottom: theme.spacing(1),
        width: '250px',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
    roleButton: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    warning: {
        border: `1px solid ${theme.palette.error.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const defaults = {
    cmc_url: '',
    idp_metadata_url: '',
    group_roles: [],
};

const AuthSAML = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [roles, setRoles] = useState([]);
    const [current, setCurrent] = useState({ auth: { saml: { ...defaults } } });
    const daForm = useValidate({});
    const [changes, setChanges] = useState(false);
    const [kickFields, setKickFields] = useState(true);
    const externalPerms = Auth.permissions.get('AuthenticationExternal');

    useEffect(() => {
        RolesService.getRoles().then(res => {
            setRoles(res.data);
        });
    }, []);

    useEffect(() => {
        setKickFields(false);
        if (systemSettings && Object.prototype.hasOwnProperty.call(systemSettings, 'auth')) {
            const s = _cloneDeep(systemSettings);

            s.auth.okta.enabled = false;
            s.auth.saml.enabled = true;
            s.auth.ldap.disabed = true;

            setCurrent(s);
        }
    }, [systemSettings]);

    useEffect(() => {
        if (!current || !current.auth) return;
        setChanges(!_isEqual(current.auth, systemSettings.auth));
    }, [current, systemSettings]);

    const handleIDP = (e, validID, valid) => {
        validID && daForm.set({ ...daForm.get(), [validID]: valid });

        const c = { ...current };
        c.auth.saml.idp_metadata_url = e.target.value;
        setCurrent(c);
    };

    const handleCMC = (e, validID, valid) => {
        validID && daForm.set({ ...daForm.get(), [validID]: valid });

        const c = { ...current };
        c.auth.saml.cmc_url = e.target.value;
        setCurrent(c);
    };

    const handleAddAuthMapping = () => {
        const c = { ...current };
        if (!c.auth.saml.group_roles) c.auth.saml.group_roles = [];
        c.auth.saml.group_roles.push({ query: '', roles: [] });
        setCurrent(c);
    };

    const handleRemoveAuthMapping = (idx) => {
        const c = { ...current };
        c.auth.saml.group_roles.splice(idx, 1);
        setCurrent(c);
    };

    const handleAuthMappingGroup = (e, idx, validID, valid) => {
        validID && daForm.set({ ...daForm.get(), [validID]: valid });

        const c = { ...current };
        c.auth.saml.group_roles[idx].group = e.target.value;
        setCurrent(c);
    };

    const handleAuthMapping = (e, idx) => {
        const c = { ...current };
        c.auth.saml.group_roles[idx].roles = e.target.value;
        setCurrent(c);
    };

    const handleRemoveRole = (mapping, role) => {
        const c = { ...current };
        c.auth.saml.group_roles[mapping].roles.splice(role, 1);
        setCurrent(c);
    };

    const handleRemoveValidator = (validID) => {
        const s = daForm.get();
        delete s[validID];
        daForm.set(s);
    };

    const handleCancel = () => {
        setCurrent(_cloneDeep(systemSettings));
        navigate('/system/configuration');
    };

    const handleSave = () => {
        updateSystemSettings({ auth: current.auth });
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <Box className={classes.warning}>
                    This change requires the CMC login service to be restarted. This will occur almost
                    immediately after saving these changes and it may momentarily disrupt the UI service.
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Validator
                        allowEmpty={!current.auth.saml.enabled}
                        disabled={!externalPerms.write}
                        errorText="IdP metadata URL cannot be empty"
                        label="IdP Metadata URL"
                        kick={kickFields}
                        fullWidth
                        placeholder='https://xxx.okta.com/app/xxx/xxx/slo/saml'
                        value={current.auth.saml.idp_metadata_url}
                        onChange={handleIDP}
                        onRemoval={handleRemoveValidator}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Validator
                        allowEmpty={!current.auth.saml.enabled}
                        disabled={!externalPerms.write}
                        errorText="Redirect URL cannot be empty"
                        label="Login Redirect URL"
                        kick={kickFields}
                        fullWidth
                        placeholder='https://cmc'
                        value={current.auth.saml.cmc_url}
                        onChange={handleCMC}
                        onRemoval={handleRemoveValidator}
                    />
                </Box>
            </Grid>
            <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                    <Box display="inline-flex">
                        <Typography variant="h6">Authorization Mappings</Typography>
                    </Box>
                    {externalPerms.write &&
                        <Button className={classes.roleButton} variant="contained" color="primary" aria-label="add mapping" onClick={handleAddAuthMapping}><AddIcon /> Add</Button>
                    }
                </Grid>
                <Grid container spacing={2} item xs={12}>
                    <Grid item xs={6}>
                        <Typography>Identify Group</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography>OpenText™ NDR Roles</Typography>
                    </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                    {(current.auth.saml.group_roles || []).map((v, i) => {
                        return (
                            <React.Fragment key={'saml-auth-map-' + i}>
                                <Grid item xs={6}>
                                    <Validator
                                        allowEmpty={!current.auth.saml.enabled}
                                        disabled={!externalPerms.write}
                                        errorText="Group name cannot be empty"
                                        fullWidth
                                        value={v.group || ''}
                                        onChange={(e, validID, valid) => { handleAuthMappingGroup(e, i, validID, valid) }}
                                        onRemoval={handleRemoveValidator}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl className={classes.select}>
                                        <InputLabel id={'saml-role-select-label-' + i}>Role</InputLabel>
                                        <Select
                                            disabled={!externalPerms.write}
                                            labelId="role-select-label"
                                            id={'role-select-id-' + i}
                                            value={v.roles}
                                            multiple
                                            onChange={(e) => { handleAuthMapping(e, i) }}
                                            input={<Input id={'saml-role-select-input-' + i} />}
                                            renderValue={(rolesSelected) => (
                                                <div className={classes.chips}>
                                                    {rolesSelected.map((v, j) => {
                                                        return <Chip
                                                            key={'role-chip-' + j}
                                                            label={v}
                                                            className={classes.chip}
                                                            onMouseDown={e => { e.stopPropagation() }}
                                                            onDelete={() => { handleRemoveRole(i, j) }}
                                                        />;
                                                    })}
                                                </div>
                                            )}
                                            MenuProps={MenuProps}
                                        >
                                            {roles.map((v, i) => { return <MenuItem key={'role-select-' + i} value={v.name}>{v.name} - {v.description}</MenuItem> })}
                                        </Select>
                                    </FormControl>
                                    {externalPerms.write &&
                                        <Button className={classes.roleButton} variant="contained" color="secondary" aria-label="remove mapping" onClick={() => { handleRemoveAuthMapping(i) }}><RemoveIcon /> Remove</Button>
                                    }
                                    <FormHelperText>Mapped roles. When the query returns one or more values, the user is granted these roles.</FormHelperText>
                                </Grid>
                            </React.Fragment>
                        );
                    })}
                </Grid>
                {externalPerms.write && <Grid item xs={12}>
                    <Button
                        className={classes.roleButton}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={handleSave}
                        disabled={!changes || !checkValidity(daForm.get())}>
                        Save
                    </Button>
                    <Button
                        className={classes.roleButton}
                        variant="contained"
                        color="secondary"
                        aria-label="cancel"
                        onClick={handleCancel}
                        // disabled={!changes}
                    >
                        Cancel
                    </Button>
                </Grid>}
            </Grid>
        </Grid>
    );
};

export default AuthSAML;
